import { mergeRegister } from "@lexical/utils";
import {
  $createNodeSelection,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $setSelection,
  COMMAND_PRIORITY_NORMAL,
  defineExtension,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_ESCAPE_COMMAND,
  SELECT_ALL_COMMAND,
  type LexicalCommand,
  type LexicalExtension,
  type LexicalNode,
  type NodeKey,
} from "lexical";
import type { BlockId } from "../../core/types";
import { BlockIdExtension } from "../block-id/extension";
import { $deleteBlocks } from "../block-id/operations";
import { $getBlockId, $isIdentified } from "../block-id/state";

export interface BlockSelectionState {
  /** Root-level blocks, in document order. Empty when the selection is text, or nothing. */
  selected: readonly BlockId[];
}

export interface BlockSelectionOutput {
  subscribe(listener: () => void): () => void;
  getState(): BlockSelectionState;
  /** Selects exactly these root-level blocks. IDs that name anything else are ignored. */
  select(blockIds: readonly BlockId[]): void;
  /** Selects one block, or with `extend` everything from the anchor of the selection to it. */
  selectBlock(blockId: BlockId, extend: boolean): void;
  /** Back to a text caret, at the end of the block the selection was last extended to. */
  clear(): void;
  /**
   * For `register` only. The two ends of a range of blocks, by node key: keys never leave the
   * package, and the store above speaks block IDs.
   */
  internal: {
    ends: { anchor: NodeKey | null; focus: NodeKey | null };
    publish(selected: readonly BlockId[]): void;
  };
}

const EMPTY: BlockSelectionState = { selected: [] };

/** The root-level blocks of a NodeSelection in document order, or `null` if it is anything else. */
export function $selectedBlocks(): LexicalNode[] | null {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) return null;
  const root = $getRoot();
  const nodes = selection.getNodes();
  if (nodes.length === 0) return null;
  if (!nodes.every((node) => root.is(node.getParent()) && $isIdentified(node))) return null;
  return nodes.toSorted((a, b) => a.getIndexWithinParent() - b.getIndexWithinParent());
}

function $select(nodes: readonly LexicalNode[]): void {
  const selection = $createNodeSelection();
  for (const node of nodes) selection.add(node.getKey());
  $setSelection(selection);
}

const $rootBlock = (blockId: BlockId): LexicalNode | null =>
  $getRoot()
    .getChildren()
    .find((node) => $isIdentified(node) && $getBlockId(node) === blockId) ?? null;

/** Every root block from one to the other, inclusive, whichever comes first. */
function $between(a: LexicalNode, b: LexicalNode): LexicalNode[] {
  const [from, to] = [a.getIndexWithinParent(), b.getIndexWithinParent()].toSorted((x, y) => x - y);
  return $getRoot()
    .getChildren()
    .slice(from, to! + 1);
}

/** A deterministic text caret for leaving block selection: the end of `node`, or around it. */
function $caretAt(node: LexicalNode | null): void {
  if (node === null) return void $getRoot().selectEnd();
  if ($isElementNode(node)) node.selectEnd();
  else (node.getNextSibling() ?? node.getPreviousSibling() ?? $getRoot()).selectStart();
}

/**
 * Block selection is Lexical's own NodeSelection over root-level blocks: rich-text already clears
 * it on a click elsewhere and the clipboard already copies such blocks whole, so no custom
 * selection class is needed. A parent and its descendant can never both be selected, because only
 * root-level blocks ever are.
 *
 */
export const BlockSelectionExtension: LexicalExtension<
  Record<never, never>,
  "z-editor/block-selection",
  BlockSelectionOutput,
  unknown
> = defineExtension({
  name: "z-editor/block-selection",
  dependencies: [BlockIdExtension],
  build(editor): BlockSelectionOutput {
    const listeners = new Set<() => void>();
    let state = EMPTY;
    const ends: { anchor: NodeKey | null; focus: NodeKey | null } = { anchor: null, focus: null };
    const publish = (selected: readonly BlockId[]): void => {
      const same =
        selected.length === state.selected.length &&
        selected.every((id, index) => id === state.selected[index]);
      if (same) return;
      state = selected.length === 0 ? EMPTY : { selected };
      for (const listener of listeners) listener();
    };
    const update = (work: () => void): void => editor.update(work, { discrete: true });
    return {
      subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
      getState: () => state,
      select: (blockIds) =>
        update(() => {
          const wanted = new Set(blockIds);
          const nodes = $getRoot()
            .getChildren()
            .filter((node) => {
              const id = $isIdentified(node) ? $getBlockId(node) : null;
              return id !== null && wanted.has(id);
            });
          if (nodes.length === 0) return;
          ends.anchor = nodes[0]!.getKey();
          ends.focus = nodes[nodes.length - 1]!.getKey();
          $select(nodes);
        }),
      selectBlock: (blockId, extend) =>
        update(() => {
          const target = $rootBlock(blockId);
          if (target === null) return;
          const anchor = extend && ends.anchor !== null ? $getNodeByKey(ends.anchor) : null;
          const attached = anchor !== null && anchor.isAttached() ? anchor : null;
          if (attached === null) ends.anchor = target.getKey();
          ends.focus = target.getKey();
          $select(attached === null ? [target] : $between(attached, target));
        }),
      clear: () =>
        update(() => {
          if ($selectedBlocks() === null) return;
          $caretAt(ends.focus === null ? null : $getNodeByKey(ends.focus));
        }),
      internal: { ends, publish },
    };
  },
  register(editor, _config, state) {
    const { ends, publish } = state.getOutput().internal;

    /** Runs only while blocks are selected; text selection never sees any of this. */
    const whenSelected =
      (handle: (blocks: LexicalNode[], event: KeyboardEvent) => void) =>
      (event: KeyboardEvent | null): boolean => {
        const blocks = $selectedBlocks();
        if (blocks === null || event === null) return false;
        // A selection made elsewhere (a click on a rule or an attachment) says nothing of its ends,
        // and what is remembered here may be from a selection long gone.
        const keys = new Set(blocks.map((block) => block.getKey()));
        if (ends.anchor === null || !keys.has(ends.anchor) || !keys.has(ends.focus ?? "")) {
          ends.anchor = blocks[0]!.getKey();
          ends.focus = blocks[blocks.length - 1]!.getKey();
        }
        event.preventDefault();
        handle(blocks, event);
        return true;
      };

    const $step = (blocks: LexicalNode[], event: KeyboardEvent, direction: 1 | -1): void => {
      const focus = (ends.focus === null ? null : $getNodeByKey(ends.focus)) ?? blocks[0]!;
      const next = direction === 1 ? focus.getNextSibling() : focus.getPreviousSibling();
      if (next === null) return;
      const anchor = ends.anchor === null ? null : $getNodeByKey(ends.anchor);
      ends.focus = next.getKey();
      if (event.shiftKey && anchor !== null && anchor.isAttached()) {
        return $select($between(anchor, next));
      }
      ends.anchor = next.getKey();
      $select([next]);
    };

    const $remove = (blocks: LexicalNode[]): void => {
      const after = blocks[blocks.length - 1]!.getNextSibling();
      const before = blocks[0]!.getPreviousSibling();
      const ids = blocks.map((block) => $getBlockId(block)).filter((id) => id !== null);
      $deleteBlocks(ids);
      if (after !== null && after.isAttached()) after.selectStart();
      else if (before !== null && before.isAttached()) $caretAt(before);
      else $getRoot().selectStart();
    };

    const keys: [LexicalCommand<KeyboardEvent>, (b: LexicalNode[], e: KeyboardEvent) => void][] = [
      [KEY_ARROW_DOWN_COMMAND, (blocks, event) => $step(blocks, event, 1)],
      [KEY_ARROW_UP_COMMAND, (blocks, event) => $step(blocks, event, -1)],
      [KEY_BACKSPACE_COMMAND, $remove],
      [KEY_DELETE_COMMAND, $remove],
      [KEY_ESCAPE_COMMAND, () => $caretAt(ends.focus === null ? null : $getNodeByKey(ends.focus))],
    ];

    return mergeRegister(
      ...keys.map(([command, handle]) =>
        editor.registerCommand(command, whenSelected(handle), COMMAND_PRIORITY_NORMAL),
      ),
      editor.registerCommand(
        SELECT_ALL_COMMAND,
        (event) => {
          if ($selectedBlocks() === null) return false;
          event.preventDefault();
          const all = $getRoot().getChildren();
          ends.anchor = all[0]?.getKey() ?? null;
          ends.focus = all[all.length - 1]?.getKey() ?? null;
          $select(all);
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      ),
      editor.registerUpdateListener(({ editorState }) => {
        publish(
          editorState.read(() =>
            ($selectedBlocks() ?? [])
              .map((node) => $getBlockId(node))
              .filter((id): id is BlockId => id !== null),
          ),
        );
      }),
    );
  },
});
